// Inicialización de react-i18next.
//
// Decisións (ver docs/I18N_PLAN.md §1):
//   - Library: react-i18next (estándar React).
//   - Source of truth: galego ("gl"). Fallback automático.
//   - Idiomas soportados: ver SUPPORTED_LANGUAGES (sincronizado co backend).
//   - Detección por orden:
//       1. ?lng= no querystring (override manual + share-friendly)
//       2. Cookie "leyro_lang" (preferencia previa)
//       3. localStorage "leyro_lang" (mesmo, redundancia client)
//       4. navigator (Accept-Language do browser)
//       5. Fallback "gl"
//     A preferencia do user logueado (User.preferredLanguage) aplícase aparte
//     desde AuthContext via i18n.changeLanguage(); non vai no detector porque
//     este corre antes de coñecer o user.
//   - Namespaces: empezamos só con "common"; engadiranse máis na fase 3 do
//     plan (booking, listings, profile, errors...).
//   - Recursos: importados estaticamente neste arquivo. Cando o número de
//     idiomas/namespaces sexa grande, migrar a i18next-http-backend (lazy
//     load por idioma+namespace).

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// X-2.1.c: namespaces comúns (common, errors, legal) viven en @leyro/i18n
// para reutilización móbil. O resto son web-só.
import glCommon from "@leyro/i18n/locales/gl/common.json";
import esCommon from "@leyro/i18n/locales/es/common.json";
import glErrors from "@leyro/i18n/locales/gl/errors.json";
import esErrors from "@leyro/i18n/locales/es/errors.json";
import glLegal from "@leyro/i18n/locales/gl/legal.json";
import esLegal from "@leyro/i18n/locales/es/legal.json";

import glAuth from "./gl/auth.json";
import esAuth from "./es/auth.json";
import glHome from "./gl/home.json";
import esHome from "./es/home.json";
import glListings from "./gl/listings.json";
import esListings from "./es/listings.json";
import glBooking from "./gl/booking.json";
import esBooking from "./es/booking.json";
import glTrips from "./gl/trips.json";
import esTrips from "./es/trips.json";
import glOwner from "./gl/owner.json";
import esOwner from "./es/owner.json";
import glProfile from "./gl/profile.json";
import esProfile from "./es/profile.json";
import glInfo from "./gl/info.json";
import esInfo from "./es/info.json";
import glAdmin from "./gl/admin.json";
import esAdmin from "./es/admin.json";
import glPublish from "./gl/publish.json";
import esPublish from "./es/publish.json";
import glApp from "./gl/app.json";
import esApp from "./es/app.json";
import glValidators from "./gl/validators.json";
import esValidators from "./es/validators.json";

export const SUPPORTED_LANGUAGES = ["gl", "es"];
export const FALLBACK_LANGUAGE = "gl";
export const LANGUAGE_COOKIE = "leyro_lang";
export const LANGUAGE_STORAGE_KEY = "leyro_lang";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      gl: {
        common: glCommon,
        auth: glAuth,
        home: glHome,
        listings: glListings,
        booking: glBooking,
        trips: glTrips,
        owner: glOwner,
        profile: glProfile,
        errors: glErrors,
        info: glInfo,
        legal: glLegal,
        admin: glAdmin,
        publish: glPublish,
        app: glApp,
        validators: glValidators,
      },
      es: {
        common: esCommon,
        auth: esAuth,
        home: esHome,
        listings: esListings,
        booking: esBooking,
        trips: esTrips,
        owner: esOwner,
        profile: esProfile,
        errors: esErrors,
        info: esInfo,
        legal: esLegal,
        admin: esAdmin,
        publish: esPublish,
        app: esApp,
        validators: esValidators,
      },
    },
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true, // "es-ES" → "es", "pt-BR" → "pt"
    defaultNS: "common",
    ns: ["common", "auth", "home", "listings", "booking", "trips", "owner", "profile", "errors", "info", "legal", "admin", "publish", "app", "validators"],
    interpolation: {
      escapeValue: false, // React xa escape os strings interpolados
    },
    detection: {
      order: ["querystring", "cookie", "localStorage", "navigator"],
      lookupQuerystring: "lng",
      lookupCookie: LANGUAGE_COOKIE,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["cookie", "localStorage"],
      cookieMinutes: 525_600, // 365 días
      cookieOptions: { path: "/", sameSite: "Lax" },
    },
    react: {
      useSuspense: true, // <Suspense> en main.jsx esperará á carga inicial
    },
  });

// Suxestión por geo-IP só na primeira visita (sen cookie). Non bloquea o
// render — corre asincrónico tras inicializar e, se a IP do request está
// nunha provincia galega, pon "gl"; se está noutra rexión española, pon
// "es"; outros, fallback. O resultado garda en cookie automaticamente
// (i18next-browser-languagedetector escóitao en changeLanguage).
if (typeof document !== "undefined" && !document.cookie.includes(`${LANGUAGE_COOKIE}=`)) {
  // Vite proxy /api → backend. Idéntica chamada en prod.
  fetch("/api/i18n/suggest")
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data?.lang) return;
      if (!SUPPORTED_LANGUAGES.includes(data.lang)) return;
      if (data.lang !== i18n.language) i18n.changeLanguage(data.lang);
    })
    .catch(() => {
      // Silent fallback — o detector xa puxo o idioma desde Accept-Language.
    });
}

export default i18n;
